/**
 *  Date    : 2018/8/23
 *  Author  : CastileMan
 *  Declare : 初进系统的路由参数整合，保存cookie参数
 *  examples:
 *  const {adviserId, source_type} = Locator.query;
 *  const entry = Locator.entry;
 *  const {pathname = '', query = {}} = Locator.parse('https://app.raysgo.com/live/W23/C1404/A1020181/lesson/2691?tableId=1727&name=1');
 */

import cookies from '@/utils/cookie';
import Utils from '@/utils/utils';
import * as config from '@/config';

const sessionStorage = window.sessionStorage;

const ENTRY = 'ENTRY_RAYS_h5';      // 入口链接在session中的key值
const SESSION_QUERY = 'SESSION_QUERY';    // query在session中的key值
const URL_REG = /^(?:([^\/]+?)\/\/)?([^\/:]*(?:\:([\d]*))?)([^\?]*)(\?.*)?/;    // 解析url的正则

const Locator = {
  // 保存初入系统的链接参数
  save: (locationState) => {

    let params = locationState.match.params;  // W/officialAccountsId，C/channelId
    let query = {};   // 初入系统的query中的参数
    let entry = {};   // 用户进入系统的链接

    let sessionEntry = sessionStorage.getItem(ENTRY) || '';
    // 已保存过query，直接读取session中保存的值
    if(sessionEntry) {
      entry = sessionEntry;
      query = Utils.params2json(sessionStorage.getItem(SESSION_QUERY) || '');
    } else {     // 初次进入，保存参数
      // 保存入口路径
      entry = window.location.href;
      sessionStorage.setItem(ENTRY, entry);

      const { userCode, wechatUserId } = cookies.get();
      const locationQuery = locationState.query;

      // 整合query
      let adviserId = '';
      let sceneId = '';
      let sourceType = locationQuery.source_type || '';

      // 整合上级参数
      let { source_user: sourceUser = '', first_td: firstTd = '', second_td: secondTd = '' } = locationQuery;
      // 分享链接
      if([Locator.SOURCE_TYPE.WECHAT_SHARE, Locator.SOURCE_TYPE.SHARE_IMAGE].includes(sourceType) && sourceUser) {
        // 点击他人分享链接
        if(sourceUser !== wechatUserId) {
          // 第二上级是来源用户的第一上级
          secondTd = firstTd;
          // 第一上级是来源用户id
          firstTd = sourceUser;
        }
      } else {
        sourceUser = null;
        firstTd = null;
        secondTd = null;
      }

      // 路由的查询参数对adviserId、sceneId进行覆盖
      if(locationQuery.sceneId > 0) {
        sceneId = locationQuery.sceneId;
      }
      if(locationQuery.adviserId > 0) {
        adviserId = locationQuery.adviserId;
      }
      // 处理链接上的userCode参数与cookie中参数不同的情况（点进了别人复制的链接 or 直接转发扫码消息，这个时候cookie不存sceneId和sourceType）
      if(locationQuery.userCode && locationQuery.userCode !== userCode) {
        sceneId = null;
        sourceType = null;
        sourceUser = null;
        firstTd = null;
        secondTd = null;
      }

      // 进入系统的链接的查询参数存入session中
      query = Utils.searchAssign({
        ...locationQuery,
        adviserId,
        sceneId,
        source_type: sourceType,
        source_user: sourceUser,
        first_td: firstTd,
        second_td: secondTd
      });

      sessionStorage.setItem(SESSION_QUERY, Utils.json2params(query));
    }

    // 保存cookie
    cookies.setItem({
      adviserId: query.adviserId,
      sceneId: query.sceneId,
      bookGroupId: query.bookGroupId,
      sourceType: query.source_type
    }, config.COOKIE, config.cookiePath);

    if(query.formRaysVision) {
      sessionStorage.setItem('FROM_RAYS_VISION_WEAPP', 1);
    }

    defineProperty('params', Object.assign(params));
    defineProperty('query', Object.assign(query));
    defineProperty('entry', entry);
  },

  // 来源类型常量
  SOURCE_TYPE: {
    QRCODE: 'QRCODE',
    WECHAT_SHARE: 'WECHAT_SHARE',
    SHARE_IMAGE: 'SHARE_IMAGE',
    ALIPAY_SHARE: 'ALIPAY_SHARE'
  },

  // 解析url链接，返回参数与window.location的解析相同
  parse: (href = '') => {
    const result = href.match(URL_REG);
    if(result) {
      let [protocol = '', hostname = '', port = '', pathname = '', search = ''] = result;
      // 整合协议
      let relative = !protocol && !hostname;
      if(!relative) {
        protocol = protocol || 'http:';
      }

      // 整合查询
      let query = {};
      if(search.length > 1) {
        query = Utils.params2json(search.slice(1));
      }

      // 判断是否与当前url一致
      let matchCurrentUrl = false;
      if(relative) {
        matchCurrentUrl = (pathname === window.location.pathname && search === window.location.search);
      } else {
        matchCurrentUrl = (href === window.location.href);
      }

      return {
        protocol,
        hostname,
        port,
        origin: `${protocol ? `${protocol}//` : ''}${hostname}`,
        pathname,
        href,
        search,
        query,
        relative,               // 是否为相对链接
        matchCurrentUrl        // 是否与当前页面url相同
      };
    } else {
      console.error('invalid url: ', href);
      return {};
    }
  },

  /**
   * 外链跳转，action为PUSH
   * 跳转前会清除session的参数
   * **/
  push: (url = '') => {
    try {
      window.history.replaceState(null, '', padQuery(window.location.href));
    } catch(err) {
      console.error(err);
    }
    Locator.clear();
    window.location = padQuery(url);
  },

  /**
   * 同push，action为REPLACE
   * **/
  replace: (url = '') => {
    Locator.clear();
    window.replace(padQuery(url));
  },

  /**
   * 清除session中的参数
   * **/
  clear: () => {
    sessionStorage.removeItem(ENTRY);
    sessionStorage.removeItem(SESSION_QUERY);
  }
};

function defineProperty(key, value) {
  Object.defineProperty(Locator, key, {
    value: value,
    writable: false,
    configurable: false
  });
}

/**
 * 跳转之前，拼接路径参数
 * @param url：要跳转的链接
 * @return {string}：拼接参数后的链接
 */
function padQuery(url = '') {
  const { adviserId, sceneId, sourceType, wechatUserId, firstTd, secondTd } = cookies.get();
  return Utils.padQuery(url, {
    adviserId,
    sceneId,
    source_type: sourceType,
    source_user: wechatUserId,
    first_td: firstTd,
    second_td: secondTd
  });
}

export default Locator;
